/**
 * The wire shapes the portal exchanges with the Worker.
 *
 * Split out of lib/hooks.ts, which was 1967 lines and imported by 154 of the app's 335 files - and
 * 114 of those 170 imports wanted nothing but a type. A type-only importer used to pull the whole
 * data layer into context to name one interface; now it pulls this file, which has no imports and
 * no runtime at all.
 *
 * CONSUMABLE_ARTEFACTS lives here rather than with the hooks because ConsumableArtefact is derived
 * from it with `typeof` - leaving it behind would mean importing a value back from hooks.ts and
 * recreating the cycle in the opposite direction.
 *
 * Keep this file import-free. A type here needing something from a feature folder is a sign it
 * belongs in that feature, not in the shared wire vocabulary.
 */

/**
 * Tool-agnostic finding category surfaced by the API (M4+). The UI groups and
 * labels findings by this abstract category and never exposes the underlying
 * scanner tool name (trivy/kics/tflint/trufflehog/checkov/etc.).
 */
export type FindingCategory = 'secret' | 'misconfiguration' | 'malware' | 'exposure' | 'vulnerability'

// ── Typed API calls ───────────────────────────────────────────────────────────

export interface UserProfile {
  user: { id: string; email: string; created_at: number }
  is_new_user: boolean
}

export type ScanStatus = 'pending' | 'processing' | 'published' | 'failed'

export interface VersionListItem {
  version: string
  status: ScanStatus
  published_at: number
  deprecated_at: number | null
  module_deprecated: boolean
  scan_completed_at: number | null
  download_count: number
  last_downloaded_at: number | null
  critical_count: number
  high_count: number
  low_count: number
  consumable: boolean
  consumable_override: boolean
  manually_blocked: boolean
  block_reason: string | null
  blocked_at: number | null
  blocked_by_name: string | null
  policy_block_summary: string | null
  // VCS source provenance; null unless published via VCS OIDC trust.
  vcs_provider: string | null
  vcs_repository: string | null
  vcs_ref: string | null
  vcs_ref_type: string | null
  vcs_sha: string | null
  vcs_commit_url: string | null
}

export type ScanSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info'

export interface ScanFinding {
  id: string
  category: FindingCategory
  severity: ScanSeverity
  rule_id: string | null
  title: string
  description: string | null
  file_path: string | null
  line_start: number | null
  resource: string | null
  remediation: string | null
}

export interface ModuleListItem {
  id: string
  name: string
  provider: string
  description: string | null
  latest_version: string | null
  latest_scan_status: ScanStatus | null
  latest_critical: number
  latest_high: number
  latest_low: number
  download_count: number
  version_count: number
  deprecated: boolean
  blocked: boolean
  block_reason: string | null
  /** Module-level tri-state serve override (migration 093): null = Auto (policy decides). */
  serve_override: ServeOverrideAction
  serve_override_reason: string | null
  bulk_rescan_requested_at: number | null
  created_at: number
  // VCS source provenance of the latest version; null unless published via VCS OIDC trust.
  latest_vcs_provider: string | null
  latest_vcs_repository: string | null
  latest_vcs_ref: string | null
  latest_vcs_ref_type: string | null
  latest_vcs_sha: string | null
  latest_vcs_commit_url: string | null
}

export interface ModuleVersion {
  id: string
  version: string
  status: ScanStatus
  deprecated_at: number | null
  deprecated_message: string | null
  published_by: string
  published_at: number
}

export interface RegistryVersion {
  id: string
  /** modules.id — module instance identity, used as the policy-override instance_key. */
  module_id?: string
  namespace: string
  name: string
  version: string
  published_at: string
  description: string | null
  deprecated: boolean
  deprecated_message: string | null
}

export interface PolicyResultItem {
  policy_id: string
  policy_name: string
  passed: boolean
  failure_msg: string | null
  evaluated_at: number
}

export interface VersionDetail extends RegistryVersion {
  status: ScanStatus
  summary: {
    critical: number
    high: number
    medium: number
    low: number
  }
  findings: ScanFinding[]
  usage_example: string | null
  readme: string | null
  consumable: boolean
  consumable_override: boolean
  manually_blocked: boolean
  block_reason: string | null
  blocked_by_name: string | null
  blocked_at: number | null
  policy_results: PolicyResultItem[]
}

/** The artefact a policy is authored against. Each policy targets exactly one. */
export type PolicyArtefactType = 'module' | 'state' | 'provider' | 'plan'

export interface PolicyItem {
  id: string
  tenant_id: string
  name: string
  description: string | null
  rego: string
  is_enabled: number
  is_enforcing: number
  artefact_type: PolicyArtefactType
  /** null = org-baseline (inheritable); a value = identity-custom, attached to one artefact
   *  identity (module / workspace / provider / plan). Present on instance reads. */
  instance_key: string | null
  /** 1 = this row IS the org Danger-zone consumption kill switch for its artefact_type, not an
   *  authored rule. Read-only everywhere: the generic toggle/edit/delete endpoints answer 409, and
   *  only the owner-gated Danger zone can change it. Its eval rollup is always 0/0 because nothing
   *  is evaluated against it - it is enforced on the serve path. */
  is_block_all: number
  created_by: string
  created_at: number
  updated_at: number
  /** Compliance rollup (present on artefact-scoped list reads): evaluated versions failing
   *  this policy, and total evaluated. */
  eval_failing?: number
  eval_total?: number
  /** Comma-joined DISTINCT names of non-compliant artefacts (rendered as the hover list). */
  eval_failing_names?: string | null
}

export interface MachineClient {
  id: string
  oidc_client_id: string
  oidc_app_id: string | null
  name: string
  description: string | null
  is_global: number
  created_by: string
  created_at: number
  last_rotated_at: number | null
  last_used_at: number | null
  last_source_ip: string | null
  last_country: string | null
  last_city: string | null
}

export interface Member {
  user_id: string
  email: string
  role: string
  joined_at: number
  last_seen_at: number | null
}

export interface TenantAnalytics {
  total_downloads: number
  downloads_last_30_days: number
  top_modules: Array<{
    tenant_name: string
    namespace: string
    name: string
    provider: string
    latest_version: string | null
    download_count: number
  }>
  daily_series: Array<{ date: string; count: number }>
  state: {
    total_workspaces: number
    versions_last_30_days: number
    findings_last_30_days: number
    // Managed-resource split summed over each workspace's latest version. Optional: an older
    // worker omits them, so a worker-then-app deploy window degrades gracefully. Drive the
    // "not yet classified" UI off classified_workspaces === 0, NOT off real+internal === 0.
    real_resources?: number
    internal_resources?: number
    classified_workspaces?: number
  }
  state_daily_series: Array<{ date: string; count: number }>
  module_findings_by_severity: Array<{ severity: string; count: number }>
  state_findings_by_severity: Array<{ severity: string; count: number }>
  top_risk_modules: Array<{ namespace: string; name: string; provider: string; version: string; critical_count: number; high_count: number }>
  top_risk_workspaces: Array<{ workspace_id: string; workspace_name: string; org_id: string; org_slug: string; critical_count: number; high_count: number }>
  // Slowest applies by LOCK->push ("Runtime") duration - a long-tail indicator of bloated configs.
  slowest_applies: Array<{ workspace_name: string; serial: number; duration_ms: number; pushed_at: number; resource_count: number }>
  // Per-workspace apply runtime, longest-first. Optional: an older worker omits it, so callers must
  // guard with optional chaining (the field may be absent across a worker-then-app deploy window).
  workspace_runtime?: Array<{ workspace_name: string; max_duration_ms: number; avg_duration_ms: number }>
  // Top-5 trend charts: per-entity daily series over the last 90 days, ordered by total
  // descending (the fixed hue-assignment order). Optional: an older worker omits them, in
  // which case the trend cards render their empty state. Modules are named "module/provider".
  top_module_download_series?: Array<{ name: string; total: number; series: Array<{ date: string; count: number }> }>
  top_workspace_push_series?: Array<{ name: string; total: number; series: Array<{ date: string; count: number }> }>
  // Drill-down for the Open-findings donuts: affected artifacts per severity, worst first,
  // capped server-side (total_artifacts carries the uncapped count for "+ N more").
  // Optional: an older worker omits them and the popovers simply don't render.
  module_findings_artifacts?: Array<{
    severity: string
    total_artifacts: number
    artifacts: Array<{ namespace: string; name: string; provider: string; version: string; count: number }>
  }>
  state_findings_artifacts?: Array<{
    severity: string
    total_artifacts: number
    artifacts: Array<{ org_slug: string; workspace_name: string; count: number }>
  }>
  // Continuous-patching exposure: superseded published versions with open critical/high
  // findings still being downloaded. Optional: an older worker omits it.
  patch_adoption?: {
    vulnerable_versions: number
    vulnerable_downloads_30d: number
    worst: Array<{
      namespace: string
      name: string
      provider: string
      version: string
      critical_count: number
      high_count: number
      downloads_30d: number
      latest_version: string | null
      latest_open_findings: number
    }>
  }
  // Cross-domain footprint: storage bytes + inventory + recency. Optional (older worker omits the
  // whole object). `*_storage_bytes` are logical sums of RECORDED sizes, not the R2 bill; NULL
  // sizes (mirror providers, pre-backfill artifacts) are excluded. `storage_measured` distinguishes
  // a real 0 B from "not measured yet" - drive the muted UI off it, never off a zero sum. The
  // aggregate flag covers the whole bar; a single segment must be driven off its own
  // `*_storage_measured`, or an unmeasured domain borrows another's and reads as a real 0 B.
  // Both are optional: a worker older than the per-domain split omits them.
  footprint?: {
    provider_storage_bytes: number
    module_storage_bytes: number
    state_storage_bytes: number
    total_storage_bytes: number
    storage_measured: boolean
    module_storage_measured?: boolean
    state_storage_measured?: boolean
    provider_storage_measured?: boolean
    module_count: number
    provider_count: number
    module_versions_retained: number
    state_versions_retained: number
    oldest_artifact_at: number | null
    newest_artifact_at: number | null
  }
}

export interface OrgItem {
  id: string
  name: string
  slug: string
  claimed_domain: string | null
  role: string
  is_demo: number
  is_personal: number
  tier: 'starter' | 'team' | 'business' | 'enterprise'
  created_at: number
}

export interface OrgMember {
  user_id: string
  email: string
  role: string
  is_billing_admin: number
  created_at: number
  /**
   * Last authenticated request by this user, platform-wide - not a login, and not scoped
   * to this org. Throttled to 5-minute resolution by the worker. `null` = never seen.
   * Optional because an older worker omits it, so an app-then-worker deploy window
   * renders "Unknown" rather than "Never".
   */
  last_seen_at?: number | null
}

/**
 * The unified dependency graph (GET /api/v1/:orgId/graph). One shape for all three artefact
 * kinds, because a graph that drew modules and state files from two differently-shaped
 * payloads ended up using the same colours to mean different things.
 *
 * Every status field is present on every kind, so a single colour scale covers the canvas.
 * Shape carries KIND, colour carries STATUS - see DepGraph.
 */
export type GraphKind = 'state' | 'module' | 'provider'

export type GraphRel = 'depends' | 'requires' | 'locks'

export type GraphUpstream = 'terraform.io' | 'opentofu.org'

export interface GraphNode {
  id: string
  kind: GraphKind
  label: string
  namespace: string
  /** Outside this org: visible, but nothing here can govern it. Computed server-side. */
  external: boolean
  // ── status ──
  blocked: boolean
  blocked_reason: string | null
  deprecated: boolean
  deprecated_message: string | null
  policy_failed: boolean
  critical_count: number
  high_count: number
  outdated: boolean
  // ── identity ──
  latest_version: string | null
  version_count: number
  module_name?: string
  module_provider?: string
  provider_type?: string
  discovered?: boolean
  // ── state nodes ──
  workspace_name?: string
  has_lock_file?: boolean
  /**
   * Lock-file provenance. Workspace-level, so it rides the NODE: on the edges it would repeat
   * once per provider the workspace pins. The edge modal reads it from the `from` node to say
   * everything it shows is as of this upload.
   */
  lock_file_uploaded_at?: number | null
  /** An email where the recorded value was a user id, otherwise the raw OIDC client id. */
  lock_file_uploaded_by?: string | null
  /** The user's own Terraform repo. NOT allowlisted server-side — render via isSafeRepoUrl. */
  infrastructure_repo_url?: string | null
  infrastructure_repo_provider?: string | null
  serial?: number | null
  vcs_provider?: string | null
  vcs_repository?: string | null
  vcs_ref?: string | null
  vcs_ref_type?: string | null
  vcs_sha?: string | null
  /**
   * Pre-built commit link from the server, on an allowlisted host. NEVER construct this in the
   * client: the point of having the worker build it is that a forged VCS claim cannot steer a
   * link we render. Render it only through isSafeRepoUrl (see SourceRepoLink).
   */
  vcs_commit_url?: string | null
}

/**
 * The relationship's own facts — the ones that belong to neither endpoint. Which version a
 * workspace actually pinned is the headline: the provider NODE can only say that something
 * pinning it is behind, never which pin or to what.
 *
 * Flat optional fields per relation, mirroring GraphNode's per-kind style. Read `rel` first;
 * each field's comment names the relations it is present for.
 *
 * `hashes` is deliberately not on the wire — see the endpoint's own note. The full set is on
 * the workspace's Providers tab.
 */
export interface GraphEdge {
  from: string
  to: string
  rel: GraphRel
  /**
   * `null` means different things by relation and the UI must word them differently: on
   * `requires` it is a `required_providers` block with no `version` (= any version), on
   * `locks` a lock file that recorded no constraint. Neither is a missing value.
   */
  constraint: string | null

  /** depends + requires: the release of the CONSUMER module that declares this. */
  declared_in_version?: string | null
  /** depends: resolves inside this registry rather than an external source. */
  is_terramantle?: boolean
  /**
   * requires + locks. The only place a registry appears, because provider nodes collapse the
   * two on purpose. `null` on a `requires` edge means "not recorded", not "neither".
   */
  upstream?: GraphUpstream | null

  // ── locks only ──
  /** The version the lock file actually pins. */
  locked_version?: string
  /** The literal lock-file address, and what tells two parallel pins apart. */
  provider_address?: string
  locked_at?: number
  /** Newest version satisfying `constraint`. Null is ambiguous — see providerFreshness. */
  latest_matching?: string | null
  /** Newest upstream version IGNORING the constraint. A different question. */
  latest_version?: string | null
  latest_version_checked_at?: number | null
  latest_lookup_error_at?: number | null
}

export interface GraphStats {
  total: number
  scanned: number
  /** Modules with no published version: mid-scan, failed, or pruned. */
  unscanned_no_versions: number
  workspaces: number
  /** Workspaces whose provider pins Terramantle cannot see. */
  workspaces_without_lock_file: number
}

export interface GraphData {
  nodes: GraphNode[]
  edges: GraphEdge[]
  stats?: GraphStats
}

export interface PolicyInstanceOverride { inherit: number; disabled: number; enforcing: number }

export interface PolicyWithOverride extends PolicyItem { override: PolicyInstanceOverride | null }

/** Policy feature enablement for an org: global master + per-artefact toggles. */
export interface PolicyEnablement {
  global: boolean
  state: boolean
  modules: boolean
  providers: boolean
  /** tier gate (Business+); enforcement requires this AND the toggles */
  opaTier: boolean
  /** how a missing trust signal is treated: 'ignore' (fail-open) | 'hard_fail' (fail-closed) */
  /** Renamed from `signal_fail_gate` with policy-input contract v2; the API returns both for
   *  one release, since the worker and this bundle do not deploy together. */
  on_missing_signal: 'ignore' | 'hard_fail'
}

/** Full settings payload from GET /policies/settings (enablement + advisory extras). */
export interface PolicySettings extends PolicyEnablement {
  /** advisory count of module versions currently blocked by an enforcing policy */
  modules_blocked_count: number
  /** advisory count of state workspaces currently blocked by policy (0 pre-migration) */
  state_blocked_count?: number
  /** advisory count of self-built provider versions currently blocked (0 pre-migration) */
  providers_blocked_count?: number
  last_changed_by: string | null
  last_changed_at: number | null
}

/** The artefact types an org can refuse wholesale. A plan push is not a consume. */
export const CONSUMABLE_ARTEFACTS = ['module', 'provider', 'state'] as const

export type ConsumableArtefact = (typeof CONSUMABLE_ARTEFACTS)[number]

export interface ConsumptionBlockEntry {
  blocked: boolean
  /** Non-null once ever armed; disarming keeps the row so old ledger entries still resolve. */
  policy_id: string | null
  /** Only meaningful while `blocked` - once disarmed it is when the switch was turned OFF. */
  changed_at: number | null
  changed_by: string | null
}

export interface ConsumptionBlock extends Record<ConsumableArtefact, ConsumptionBlockEntry> {
  /** Derived, not stored: true when all three are armed. */
  all_blocked: boolean
}

export interface AuditEventItem {
  id: string
  actor_email: string | null
  action: string
  resource_type: string
  resource_id: string | null
  metadata: string | null
  created_at: number
}

// ── State file backend ────────────────────────────────────────────────────────

export type StateScanStatus = 'pending' | 'processing' | 'published' | 'failed'

export type StateScanSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info'

export interface StateWorkspaceSummary {
  id: string
  name: string
  created_by: string
  created_at: number
  latest_serial: number | null
  resource_count: number
  /** Managed-resource split of the latest version. null = not yet classified; undefined on an older worker. */
  real_resource_count?: number | null
  internal_resource_count?: number | null
  pushed_at: number | null
  pushed_by: string | null
  scan_status: StateScanStatus | null
  lock_id: string | null
  lock_who: string | null
  /** Policy block flag (0/1). 1 => the next apply's LOCK is rejected until attested/approved.
   *  Set by a state scan block OR a plan gate block (M2). Undefined on an older worker. */
  blocked?: number
  /** Tri-state enforcement override (migration 093). null/undefined = Auto; 'block' = LOCK
   *  always rejected; 'allow' = LOCK always allowed (skips policy AND plan blocks). */
  serve_override?: ServeOverrideAction
  serve_override_reason?: string | null
  /** Finding counts for the LATEST version only (0 when unscanned). */
  critical_count: number
  high_count: number
  vuln_count: number
  /** Per-version policy compliance for the latest version. 0/0 = none evaluated. */
  policy_failing_count: number
  policy_total_count: number
  // Actor metadata for the latest version (display-only). `pushed_by` above is the legacy
  // users(id) FK - null for every bot-token push - so the UI reads these instead.
  actor_type: string | null
  actor_name: string | null
  vcs_provider: string | null
  vcs_repository: string | null
  vcs_ref: string | null
  vcs_ref_type: string | null
  vcs_sha: string | null
  vcs_commit_url: string | null
}

export interface StateVersionSummary {
  id: string
  serial: number
  resource_count: number
  /** Managed-resource split. null = not yet classified; undefined on an older worker. */
  real_resource_count?: number | null
  internal_resource_count?: number | null
  lineage: string | null
  pushed_by: string | null
  pushed_at: number
  scan_status: StateScanStatus
  critical_count: number
  high_count: number
  /** Per-version policy compliance: failing verdicts and total evaluated (0 = none evaluated). */
  policy_failing_count?: number
  policy_total_count?: number
  /** LOCK -> state-push duration ("Runtime") for the apply that produced this version, ms. Null = unmeasured. */
  push_duration_ms: number | null
  /** Source serial when this version is a restore (promote-copy); null = normal apply. */
  promoted_from_serial?: number | null
  // VCS source provenance; null unless pushed via VCS OIDC trust.
  vcs_provider: string | null
  vcs_repository: string | null
  vcs_ref: string | null
  vcs_ref_type: string | null
  vcs_sha: string | null
  vcs_commit_url: string | null
  // Actor metadata captured at push time; null for legacy rows. Display-only.
  actor_type: string | null
  actor_name: string | null
}

export interface StateFinding {
  id: string
  version_id: string
  category: FindingCategory
  severity: StateScanSeverity
  rule_id: string | null
  title: string
  description: string | null
  resource: string | null
  remediation: string | null
  meta: Record<string, unknown> | null
  created_at: number
}

export interface StateLockInfo {
  lock_id: string
  operation: string
  who: string
  created_at: number
  // Actor origin captured at lock time. PII fields (ip/name/geo) are omitted by
  // the API for reader-role callers - hence all optional.
  actor_ip?: string | null
  actor_country?: string | null
  actor_city?: string | null
  actor_type?: 'vcs' | 'bot_token' | 'human' | null
  actor_name?: string | null
}

/** One UTC day's push count. Sparse - days with no pushes are omitted, so consumers zero-fill. */
export interface WorkspacePushPoint {
  date: string
  count: number
}

export interface WorkspaceSlowApply {
  serial: number
  duration_ms: number
  pushed_at: number
  resource_count: number
  critical_count: number
}

export interface WorkspaceActivity {
  /** Server-owned window length; captions echo this rather than hardcoding 90. */
  window_days: number
  pushes: WorkspacePushPoint[]
  slowest_applies: WorkspaceSlowApply[]
  runtime: {
    measured_count: number
    median_duration_ms: number | null
    avg_duration_ms: number | null
    max_duration_ms: number | null
  }
  /** Lifetime, not windowed. */
  totals: {
    version_count: number
    first_pushed_at: number | null
    last_pushed_at: number | null
  }
}

export type PolicyInteraction =
  | 'state.lock' | 'state.push' | 'state.attest' | 'state.serve_override'
  | 'plan.push' | 'plan.approve' | 'module.download' | 'provider.download'
  /** A state READ refused by the org consumption kill switch. */
  | 'state.pull'

export interface PolicyDecision {
  id: string
  decided_at: number
  interaction: PolicyInteraction
  verdict: 'block' | 'override' | 'warn'
  source: 'builtin' | 'opa' | 'scan' | 'manual' | 'serve_override' | 'block_all'
  subject_kind: 'state_workspace' | 'state_version' | 'plan_version' | 'module_version' | 'provider_version'
  subject_id: string | null
  subject_label: string
  /**
   * Machine coordinates for the subject, so the UI can route to it (migration 128). Null on a row
   * written before that migration and whenever the coordinates did not fully resolve; NOT
   * derivable from subject_label, which is lossy display text.
   */
  subject_key: string | null
  workspace_id: string | null
  run_id: string | null
  plan_version_id: string | null
  policy_id: string | null
  policy_name: string | null
  reason: string | null
  tool: string | null
  tool_version: string | null
  actor_type: string | null
  actor_name: string | null
  req_id: string | null
  trace_id: string | null
}

/** A LOCK...UNLOCK cycle that actually acquired its lock. */
export interface TimelineRunEntry {
  kind: 'run'
  id: string
  at: number
  operation: string
  who: string | null
  serial: number | null
  status: 'active' | 'completed' | 'no_push' | 'abandoned'
  push_duration_ms: number | null
  hold_duration_ms: number | null
  plan_version_id: string | null
  version_id: string | null
}

export type TimelineEntry = TimelineRunEntry | ({ kind: 'decision'; at: number } & PolicyDecision)

export interface DecisionDailyPoint {
  date: string
  count: number
}

export interface PolicyDecisionFacets {
  interaction: Array<{ key: PolicyInteraction; n: number }>
  verdict: Array<{ key: string; n: number }>
  /** Rows matching every filter - the exact count the list is paging through. */
  total: number
}

export interface PolicyDecisionFeed {
  decisions: PolicyDecision[]
  facets: PolicyDecisionFacets
  has_more: boolean
  /**
   * Refusals per UTC day. OPTIONAL by contract: the worker sends it on the first page only (paging
   * cannot change the series), and this bundle must also tolerate a worker deployed without it -
   * the SPA and the worker do not ship together.
   */
  daily?: DecisionDailyPoint[]
  window_days: number
  limit: number
  offset: number
}

export interface DecisionLedgerFilters {
  interaction?: string
  verdict?: string
  policyId?: string
  q?: string
  /**
   * One request's refusal, as printed in a terraform/tofu terminal. A refused state LOCK shows
   * this as the Lock Info `ID` (prefixed POLICY-BLOCK-) and links straight here, so the operator
   * lands on the rows their own apply produced instead of searching the feed.
   */
  reqId?: string
}

export interface DecisionLedger {
  rows: PolicyDecision[]
  facets: PolicyDecisionFacets | null
  daily: DecisionDailyPoint[]
  windowDays: number
  /** First page of THESE filters has never landed - the only state that shows a skeleton. */
  loading: boolean
  refreshing: boolean
  error: string | null
  loadingMore: boolean
  moreError: string | null
  hasMore: boolean
  loadMore: () => void
  refetch: () => void
}

export interface PolicyCoverageItem {
  policy_id: string
  policy_name: string
  artefact_type: 'module' | 'state' | 'provider' | 'plan'
  is_enabled: number
  is_enforcing: number
  pass_count: number
  fail_count: number
  block_count: number
  last_block_at: number | null
}

export type InspectorChange = 'added' | 'removed' | 'changed' | 'unchanged'

export interface InspectorAttribute {
  key: string
  /** Absent when redacted. */
  value?: unknown
  redacted: boolean
  reason?: 'sensitive' | 'heuristic'
  value_type: string
  /** Only present in compare mode. */
  change?: InspectorChange
}

export interface InspectorInstance {
  index_key: string | number | null
  attributes: InspectorAttribute[]
  change?: InspectorChange
}

export interface InspectorResource {
  address: string
  module: string
  mode: 'managed' | 'data'
  type: string
  name: string
  provider: string
  instances: InspectorInstance[]
  change?: InspectorChange
}

export interface ResourceTreeResponse {
  version_id: string
  serial: number
  compare_version_id: string | null
  compare_serial: number | null
  resources: InspectorResource[]
  resource_count: number
  sensitive_count: number
}

/** Redacted plan-version summary. Matches the worker's PlanVersionSummary projection. */
export interface PlanVersionSummary {
  id: string
  plan_ref: string | null
  add_count: number
  change_count: number
  destroy_count: number
  replace_count: number
  noop_count: number
  drift_count: number
  sensitive_change_count: number
  attestation_hash: string
  prior_serial: number | null
  prior_lineage: string | null
  terraform_version: string | null
  scan_status: string
  created_by: string | null
  created_at: number
  actor_type: string | null
  actor_name: string | null
}

/** Redacted plan finding (M3 populates the source table; the read path is redacted-only).
 *  `meta` carries structured, value-free detail: drift (before/after redaction sentinels,
 *  provider), replacement cause, sensitive-change location. Never a concrete value. */
export interface PlanFinding {
  id: string
  category: string | null
  severity: string
  rule_id: string | null
  title: string
  description: string | null
  resource: string | null
  attr: string | null
  remediation: string | null
  meta: Record<string, unknown> | null
}

/** One persisted plan policy-evaluation result. `source` is 'builtin' (the synchronous M2 gate)
 *  or 'opa' (async advisory). `failure_msg` carries the gate reasons — redacted, never a value. */
export interface PlanPolicyResult {
  id: string
  policy_id: string | null
  policy_name: string
  source: string
  passed: boolean
  failure_msg: string | null
  evaluated_at: number
}

/** Apply-matches-review attestation verdict for a plan version (M5). The Summary tab renders
 *  verified / attested-not-approved / unverified from these flags; a CI apply job fails closed
 *  unless attested && approved && !stale. */
export interface PlanAttestation {
  attested: boolean
  approved: boolean
  prior_serial: number | null
  current_serial: number | null
  stale: boolean
  reason: string
}

/** Cross-workspace forced-replacement rollup for one provider (M5). `workspaces` = where this
 *  provider forces a replacement; `blast_radius_workspace_count` = workspaces using it at all. */
export interface PlanReplacementByProvider {
  provider: string
  replaced_resource_count: number
  workspaces: { id: string; name: string; replaced_resource_count: number }[]
  blast_radius_workspace_count: number
}

/** Built-in plan gate (M2) config: destroy threshold, denied resource types, approval flag. */
export interface PlanPolicyConfig {
  max_destroys: number | null
  destroy_denylist: string[]
  require_approval: boolean
}

// ── Org-level bot tokens ──────────────────────────────────────────────────────

export interface OrgToken extends MachineClient {
  tenant_id: string | null
  scope: 'org' | 'state_workspaces' | 'global'
  include_modules: 'none' | 'read' | 'write' | null
  include_state: 'none' | 'read' | 'write' | null
  state_grants: Array<{ workspace_id: string; workspace_name: string; permission: 'read' | 'write' }>
  created_by_email: string | null
}

// ── Pipeline Trust entries (VCS / OIDC) ───────────────────────────────────────

export interface VcsTrustEntry {
  id: string
  provider: 'github' | 'gitlab'
  subject: string
  /** Ref globs a token from this entry may PUSH from ('main', 'release/*', 'tag:v*'). null = any ref
   *  (entries created before the rule existed). [] = the stored rule failed to parse; denies every push. */
  allowed_refs: string[] | null
  created_at: number
}

/** Org network access rules (migration 134). mode 'allow' = must match a listed entry; 'deny' = must
 *  match none. applies_to 'machine' = bot tokens + CI OIDC; 'all' adds humans. */
export interface TenantAccessRules {
  enabled: boolean
  mode: 'allow' | 'deny'
  countries: string[]
  asns: number[]
  cidrs: string[]
  applies_to: 'machine' | 'all'
  /** null when no rule set has ever been saved. */
  updated_at: number | null
}

export interface AccessRulesPreview {
  sampled: number
  would_deny: number
  sources: { state_locks: number; machine_clients: number }
  denied_by_country: { country: string; count: number }[]
  window_days: number
  asn_evaluated: boolean
  note: string | null
}

/** Tri-state serve override (migration 093), org-scoped. null = Auto (policy decides);
 *  'block' = force-refuse serving regardless of policy; 'allow' = serve even when
 *  policy-blocked. Applies to providers (artefact + version), modules (module + version)
 *  and state workspaces. */
export type ServeOverrideAction = 'block' | 'allow' | null

/** Single artefact-level serve mode (migration 095): 'off' = config kept, nothing served; 'serve' =
 *  upstream + (Business+) patched versions served to the org; 'block' = hard 404. */
export type ServeMode = 'off' | 'serve' | 'block'

/** Registry a provider artefact belongs to. Part of the artefact identity (route segment
 *  `/providers/:upstream/:namespace/:type`); never null. */
export type RegistryUpstream = 'terraform.io' | 'opentofu.org'

/** One row in the unified artefact list. `mirrored_count`/`published_count` are independent
 *  per-version tallies (a version can be both), not a partition of `version_count`. */
export interface ProviderArtefactRow {
  id: string
  namespace: string
  type: string
  /** True when a lockfile / module required_providers surfaced this provider (auto-discovered);
   *  false when an admin added it by hand. */
  discovered: boolean
  /** Registry the provider is served from. Part of the identity: the same (namespace, type) may
   *  appear once per registry. */
  upstream: RegistryUpstream
  /** Gates which versions clients may pull (empty = all versions). */
  version_constraint: string
  /** VCS repo for the provider, or null when unresolved. */
  source_url: string | null
  version_count: number
  /** Versions whose upstream bytes Terramantle has cached (R2) after serving them. */
  mirrored_count: number
  /** Versions terramantle built + signed itself, whether or not they needed CVE fixes. */
  published_count: number
  /** Proper semver-max latest version, or null when no versions exist yet. */
  latest_version: string | null
  /** The registry's actual newest release (lookup-queue cache) - independent of what this org
   *  has cached; null until the lookup has run for this provider. */
  upstream_latest_version: string | null
  /** Scan counts for `latest_version` only (raw signals, no server verdict) - never an older
   *  version's, even one with findings `latest_version` doesn't have. */
  vuln_count: number
  critical_count: number
  high_count: number
  /** How many of this org's state workspaces lock this provider (any version) — the "blast
   *  radius" / in-use count, sourced from workspace lock files (NOT the same as
   *  `referenced_module_count`, which counts required_providers declarations). */
  usage_workspace_count: number
  /** true when any locked version is behind workspace_providers.latest_matching. */
  outdated: boolean
  /** Single serve mode (migration 095): 'off' = don't serve, 'serve' = upstream + patched versions
   *  served, 'block' = hard 404. */
  serve_mode: ServeMode
  /** Distinct modules whose required_providers declares this provider. */
  referenced_module_count: number
  /** Rolling served-download counts; always present, zeros until history accrues. */
  downloads: { last_30d: number; last_60d: number; last_90d: number; total: number }
  /** Cached VCS repo-health stats; null when no resolvable repo or not yet fetched. */
  vcs_stats: ProviderVcsStats | null
  /** Count of base versions terramantle has built (0 when none). Drives the "Built" column. */
  built_count: number
  /** Newest built full_version (`<base>-0.0.<rev>`) for the column; null when none. */
  latest_build_version: string | null
  /** Of the builds above, how many applied CVE fixes. 0 when none did, or when the org lacks the
   *  entitlement that reveals remediation detail. */
  patched_count: number
}

/** One workspace locking a specific provider version (M3 used-by). */
export interface ProviderUsedBy {
  workspace_id: string
  workspace_name: string
  locked_at: number
}

/** One referencing module version's required_providers declaration for this artefact. */
export interface ProviderModuleRef {
  module_id: string
  module_name: string
  /** The module's provider URL segment (modules/:name/:provider) - forms the detail link. */
  module_provider: string
  version_constraint: string
}

/**
 * Per-version scan rollup from the shared cross-org cache — the worker's `VersionScanRollup`
 * (worker/src/lib/providerScanRead.ts), mirrored once here rather than re-inlined per consumer.
 *
 * `scanned` is false only for a version listed because a workspace locks / a module references it
 * before any scan completed. Render that as "not scanned" — NEVER as "no findings".
 */
export interface VersionScanRollup {
  scanned: boolean
  malware_detected: boolean
  vuln_count: number
  critical_count: number
  high_count: number
  max_cvss: number | null
  has_unfixable_high: boolean
}

/** One vulnerability finding on a specific provider version, from the shared cross-org scan
 *  cache. `fixable` mirrors `fixed_version` being set; `primary_url`, when present, links out to
 *  the advisory. */
export interface ProviderVersionFinding {
  cve_id: string | null
  severity: string
  pkg_name: string | null
  installed_version: string | null
  fixed_version: string | null
  fixable: boolean
  cvss_score: number | null
  primary_url: string | null
}

/** One version row in the artefact detail. `servable` is the worker's authoritative verdict
 *  (bytes exist AND in-constraint AND, for a published-only version, org-entitled) — the UI
 *  must not re-derive it from the flags. The rollup counts + `used_by` are read straight off the
 *  server (never re-derived client-side). */
// Omit<..., 'malware_detected'>: this endpoint's version rows carry the rest of the rollup but
// not the malware flag, so the shared shape is narrowed rather than widened with an optional.
export interface ProviderArtefactVersion extends Omit<VersionScanRollup, 'malware_detected'> {
  version: string
  is_mirrored: boolean
  is_published: boolean
  released_at: number | null
  size_bytes: number | null
  /** Downloads served to this org's workspaces for this version in the last 30 days; 0 when none yet. */
  downloads_30d: number
  servable: boolean
  usage_workspace_count: number
  used_by: ProviderUsedBy[]
  /** Full vulnerability findings list for this version (the CVE/SBOM drill-down). */
  findings: ProviderVersionFinding[]
  has_sbom: boolean
  sbom_size: number | null
  /** Version-level tri-state serve override (migration 093): null = Auto. */
  serve_override: ServeOverrideAction
  serve_override_reason: string | null
  /** Which of this org's modules declare this provider at a constraint admitting this version. */
  referenced_by_modules: ProviderModuleRef[]
  referenced_module_count: number
  /** terramantle's own build of THIS upstream version, or null when we have not built it (or the
   *  org is not Business+, which withholds the remediation detail but not `is_published`).
   *  `fixed_count` is 0 for a version that had nothing fixable: it is still built, pinned,
   *  gate-passed, signed and scanned, which is the provenance half of the job. The full
   *  finding-level diff loads lazily from the comparison endpoint. */
  build: {
    full_version: string
    patch_rev: number
    published_at: number | null
    fixed_count: number
    report_only_count: number
    /** What the build bumped (from the lineage manifest), empty when it fixed nothing - shown on
     *  the row without a second request. The comparison endpoint reports what a SCAN verified. */
    manifest: ProviderComparisonEntry[]
    report_only: Array<{ cve_id: string; severity: string; pkg: string; fixed_in: string }>
    toolchain_bump: string | null
    fork_commit_sha: string | null
    diff_url: string | null
  } | null
}

/** One CVE entry on either side of the upstream-vs-patched comparison. */
export interface ProviderComparisonEntry {
  cve_id: string
  severity: string
  pkg: string | null
  from: string | null
  to: string | null
}

/** Full upstream-vs-patched comparison for one base version (lazy /comparison endpoint). */
export interface ProviderVersionComparison {
  base_version: string
  full_version: string
  /** False until the patched tree has a completed scan; the diff lists are then empty and
   *  `planned` (the patch manifest) is what the UI can show. */
  patched_scanned: boolean
  planned: ProviderComparisonEntry[]
  fixed: ProviderComparisonEntry[]
  remaining: ProviderComparisonEntry[]
  introduced: ProviderComparisonEntry[]
  report_only: Array<{ cve_id: string; severity: string; pkg: string; fixed_in: string }>
  provenance: {
    fork_repo: string
    fork_tag: string | null
    fork_commit_sha: string | null
    upstream_commit_sha: string | null
    diff_url: string | null
    toolchain_bump: string | null
  }
}

/** VCS repo-health stats (M6), served from the D1 cache (never a live call). Any field may be null
 *  when unresolved; `fetch_error` records the last refresh problem (e.g. 'rate_limited'). */
export interface ProviderVcsStats {
  host: string
  repo: string
  /** 1 when `repo` is a fork and the numbers below were taken from `parent_repo` (e.g. the
   *  OpenTofu registry serves hashicorp/* from opentofu/* forks). Null before the first
   *  fork-aware refresh. */
  is_fork: number | null
  parent_repo: string | null
  stars: number | null
  recent_commits: number | null
  /** true when recent_commits hit the single-page cap — render "N+", not an exact count. */
  recent_commits_capped: boolean
  latest_release: string | null
  latest_release_at: number | null
  /** Releases published in the trailing 90 days (single-page cap); null until first refresh
   *  after migration 091. */
  releases_90d: number | null
  fetched_at: number
  fetch_error: string | null
}

export interface ProviderArtefactDetail {
  id: string
  namespace: string
  type: string
  discovered: boolean
  upstream: RegistryUpstream
  version_constraint: string
  source_url: string | null
  created_at: number
  updated_at: number
  /** Artefact-level lockfile "blast radius" (M3 header enrichment). */
  usage_workspace_count: number
  usage_workspace_ids: string[]
  /** Single serve mode (migration 095): 'off' | 'serve' | 'block'. */
  serve_mode: ServeMode
  /** Distinct modules whose required_providers declares this provider, and the per-module refs. */
  referenced_module_count: number
  referenced_by: ProviderModuleRef[]
  /** VCS repo-health stats (M6), cached; null when no resolvable repo or not yet fetched. */
  vcs_stats: ProviderVcsStats | null
  /** Rolling served-download counts for the artefact. Always present; zeros until download
   *  recording accrues history. `series_90d` is the per-version daily series for the
   *  downloads chart - server-capped at the top-6 versions plus an aggregated 'other'; points
   *  are sparse (days with zero downloads omitted - the chart zero-fills). */
  downloads: {
    last_30d: number
    last_60d: number
    last_90d: number
    total: number
    series_90d: Array<{ version: string; points: Array<{ date: string; count: number }> }>
    /** Server-computed served-patched attribution over 90d (migration 095): how many downloads
     *  delivered a terramantle build vs upstream bytes. */
    patched: { patched_90d: number; total_90d: number }
  }
  versions: ProviderArtefactVersion[]
}

/** One ordered CLI step in the "Consume this version" config (M4). */
export interface ProviderConsumeStep {
  title: string
  command: string
  note?: string
}

/** Materials the worker computes to render "Consume this version". `servable` says whether
 *  `tofu init` would resolve this exact version right now (serve mode + bytes + constraint +
 *  entitlement); the config is emitted either way. */
export interface ProviderConsumeConfig {
  namespace: string
  type: string
  version: string
  /** Whether this exact version is servable now (in-constraint + bytes cached + entitled). */
  servable?: boolean
  /** Provider source address (`<host>/<namespace>/<type>` — no org id; resolved from the token). */
  source?: string
  /** The org's Terramantle registry endpoint URL (the subdomain host). */
  registry_endpoint_url?: string
  /** required_providers block pinned to the exact version, ready to paste. */
  required_providers_hcl?: string
  steps?: ProviderConsumeStep[]
}

// ── Terraform Provider Tracking ───────────────────────────────────────────────

export interface WorkspaceLockFileMeta {
  uploaded_at: number
  uploaded_by: string | null
  infrastructure_repo_url: string | null
}

export interface WorkspaceProvider {
  id: string
  provider_address: string
  namespace: string
  type: string
  upstream: 'terraform.io' | 'opentofu.org'
  current_version: string
  constraints: string
  latest_version: string | null
  latest_matching: string | null
  latest_version_checked_at: number | null
  latest_lookup_error_at: number | null
  provider_repo_url: string | null
  /** Vulnerability rollup for `current_version` — the risk actually deployed. OPTIONAL: absent on
   *  an older worker. `null` means the version has no completed scan ("not scanned"), which is a
   *  different fact from a zero-count rollup ("scanned, clean") and must render differently. */
  current_scan?: VersionScanRollup | null
  /** Same for `latest_version`: what an upgrade would land on. Always null when there is no newer
   *  version. */
  latest_scan?: VersionScanRollup | null
}

// ─── Provider build flow state (Argo-derived; routes/admin/providerFlows.ts + the /flow route) ──

/** One DAG step of a build/scan workflow. `pending` = not yet reached (the step enum is closed,
 *  so absence from the observed nodes is meaningful, not missing data). */
export interface ProviderFlowStep {
  step: string
  outcome: 'ok' | 'skipped' | 'failed' | 'in_progress' | 'pending'
  attempt: number
  started_at?: number
  finished_at?: number
  message?: string
}

/** `unknown` is an honest value: the observer (queue supervisor / Argo) could not vouch either
 *  way. The UI must render it as "state unavailable", never as pending/queued. */
export type ProviderFlowPhase =
  | 'none' | 'queued' | 'running' | 'blocked' | 'succeeded' | 'failed' | 'lost' | 'unknown'

export interface ProviderFlowState {
  phase: ProviderFlowPhase
  source: 'd1' | 'argo_live' | 'argo_archive' | 'snapshot' | 'absent'
  workflow_name: string | null
  workflow_uid: string | null
  current_step: string | null
  steps: ProviderFlowStep[]
  failure_stage: string | null
  failure_reason: string | null
  /** A gate BLOCK - the product working, not a pipeline outage. */
  blocked: boolean
  attempt: number
  dispatched_at: number | null
  observed_at: number | null
  queue_dwell_ms: number | null
  scheduler_pending_ms: number | null
}

export interface ProviderFlowItem {
  job_id: string
  namespace: string
  type: string
  version: string
  upstream: string
  requested_via: string
  triggered_at: number
  flow: ProviderFlowState
}
